import { IconBrandGoogle } from "@assets/icons";
import { getFirebaseAuth, getFirebaseGoogleAuthProvider } from "@utils/firebase";
import { signInWithPopup } from "firebase/auth";
import { Button, PropsWithClassName, cn } from "kz-ui-sdk";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthSocialErrors, AuthSocialProviderProps } from "../../../types";

const PROVIDER_NAME = "Google";

interface FirebaseGoogleAuthButtonProps extends AuthSocialProviderProps, PropsWithClassName {
  loading?: boolean;
}

const FirebaseGoogleAuthButton = ({
  onOAuthLoginSuccess,
  onOAuthLoginError,
  mode,
  loading,
  className,
}: FirebaseGoogleAuthButtonProps) => {
  const { t } = useTranslation();
  const [isSigningIn, setIsSigningIn] = useState(false);

  const { auth, provider } = useMemo(() => {
    return {
      auth: getFirebaseAuth(),
      provider: getFirebaseGoogleAuthProvider(),
    };
  }, []);

  const handleSignIn = useCallback(async () => {
    if (!auth || !provider) {
      onOAuthLoginError?.(AuthSocialErrors.SignInThirdPartyFailed);
      return;
    }

    setIsSigningIn(true);

    try {
      const result = await signInWithPopup(auth, provider);
      // Get Firebase ID token from the user object (as shown in the HTML example)
      // This is the token that Firebase Admin SDK will verify on the backend
      const idToken = await result.user.getIdToken();

      if (!idToken) {
        onOAuthLoginError?.(AuthSocialErrors.SignInThirdPartyFailed);
        return;
      }

      onOAuthLoginSuccess?.(idToken);
    } catch (error: unknown) {
      // Handle Firebase auth errors
      if (error && typeof error === "object" && "code" in error) {
        const errorCode = error.code as string;

        // User cancelled popup
        if (errorCode === "auth/popup-closed-by-user" || errorCode === "auth/cancelled-popup-request") {
          // Don't show error for user cancellation
          return;
        }
      }

      onOAuthLoginError?.(AuthSocialErrors.SignInThirdPartyFailed);
    } finally {
      setIsSigningIn(false);
    }
  }, [onOAuthLoginSuccess, onOAuthLoginError, auth, provider]);

  const btnText = useMemo(() => {
    const translationKey = mode === "login" ? "login with {{provider}}" : "sign up with {{provider}}";
    return t(translationKey, { provider: PROVIDER_NAME });
  }, [mode, t]);

  const isLoading = loading || isSigningIn;

  return (
    <Button
      variant="secondary"
      size="lg"
      icon={<IconBrandGoogle className="h-6 w-6" />}
      classes={{
        "label&": "text-content-base text-base font-normal",
      }}
      aria-label={`OAuth ${mode} with ${PROVIDER_NAME}`}
      loading={isLoading}
      onClick={handleSignIn}
      disabled={isLoading}
      className={cn("w-full", className)}
      data-element-id="button-oauth-firebase-google"
    >
      {btnText}
    </Button>
  );
};

export default FirebaseGoogleAuthButton;
